import axios from '@/lib/axios'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { get } from '@vueuse/core'
import { type MaybeRef, computed } from 'vue'
import { useToast } from 'vue-toastification'

import type { CustomAxiosRequestConfig } from '@/interfaces/customAxiosRequestConfig'

// {used, limit, remaining, periodEnd} — camelCase vía ParseToCamelAfter.
// limit/remaining null = ilimitado; usage null = empresa sin suscripción.
export interface AiReportUsageMeter {
	used: number
	limit: number | null
	remaining: number | null
	periodEnd: string
}

export interface AiReportPackageOption {
	code: string
	reports: number
	priceMxn: number
	label: string
}

interface AiReportUsageResponse {
	usage: AiReportUsageMeter | null
	packages: {
		total: number
		used: number
		remaining: number
		catalog: AiReportPackageOption[]
	}
}

const getAiReportUsage = async (
	companyId: number
): Promise<AiReportUsageResponse> => {
	const { data } = await axios.get<AiReportUsageResponse>(
		`/api/companies/${companyId}/reports/ai/usage`,
		{ errorHandling: false } as CustomAxiosRequestConfig
	)
	return data
}

/**
 * Medidor semanal de reportes IA (lunes–domingo) + créditos de paquetes extra
 * y compra. Invalidar con `invalidate()` tras cada generación para que el
 * contador avance al momento.
 */
const useAiReportUsage = (companyId: MaybeRef<number>) => {
	const toast = useToast()
	const queryClient = useQueryClient()
	const resolvedCompany = computed(() => get(companyId))

	const { data, isLoading } = useQuery({
		queryKey: computed(() => ['aiReportUsage', resolvedCompany.value]),
		queryFn: () => getAiReportUsage(resolvedCompany.value),
		enabled: computed(() => !!resolvedCompany.value),
		retry: false,
		refetchOnWindowFocus: false,
	})

	const usage = computed(() => data.value?.usage ?? null)
	const packagesRemaining = computed(() => data.value?.packages.remaining ?? 0)
	const packagesUsed = computed(() => data.value?.packages.used ?? 0)
	const packagesTotal = computed(() => data.value?.packages.total ?? 0)
	const packageCatalog = computed(() => data.value?.packages.catalog ?? [])

	const invalidate = () =>
		queryClient.invalidateQueries({ queryKey: ['aiReportUsage'] })

	const purchaseMutation = useMutation(
		(packageCode: string) =>
			axios.post(
				`/api/companies/${resolvedCompany.value}/reports/ai/packages`,
				{ package: packageCode },
				{ errorHandling: false } as CustomAxiosRequestConfig
			),
		{
			onSuccess: () => {
				toast.success('Paquete agregado. El cargo se sumará a tu suscripción.')
				invalidate()
			},
			onError: (err: unknown) => {
				const msg = (err as { response?: { data?: { message?: string } } })
					?.response?.data?.message
				toast.error(msg ?? 'No se pudo comprar el paquete.')
			},
		}
	)

	return {
		usage,
		packagesRemaining,
		packagesUsed,
		packagesTotal,
		packageCatalog,
		isLoading,
		invalidate,
		purchasePackage: purchaseMutation.mutate,
		isPurchasing: purchaseMutation.isLoading,
	}
}

export default useAiReportUsage
